import { FC, useEffect, useState } from "react"
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "react-beautiful-dnd"
import { ConfigAdminToggleRow } from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import {
  RateCardsConfigDetailsData,
  RateCardsConfigTabOption
} from "../../../../../../types"
import TermLoanRow from "./TermLoanRow"
import {
  moveTabOption,
  orderLoanTypesForDisplay,
  reorderTabOptions
} from "./utils"

interface LoanTypePanelProps {
  group: "purchase" | "refinance"
  label: string
  loanTypes: { key: string; label: string }[]
  details: RateCardsConfigDetailsData
  setDetails: (details: RateCardsConfigDetailsData) => void
}

const LoanTypePanel: FC<LoanTypePanelProps> = ({
  group,
  label,
  loanTypes,
  details,
  setDetails
}) => {
  const tabs = details.tabs?.[group] ?? []
  const [groupEnabled, setGroupEnabled] = useState(tabs.length > 0)
  const [stashedTabs, setStashedTabs] = useState<RateCardsConfigTabOption[]>([])
  const activeLoanTypeKeys = new Set(loanTypes.map(lt => lt.key))
  const selectedTabs = tabs.filter(tab => activeLoanTypeKeys.has(tab.type))
  const selectedTabTypes = new Set(selectedTabs.map(tab => tab.type))
  const orderedLoanTypes = orderLoanTypesForDisplay(loanTypes, selectedTabs)
  const selectedLoanTypes = orderedLoanTypes.filter(lt =>
    selectedTabTypes.has(lt.key)
  )
  const unselectedLoanTypes = orderedLoanTypes.filter(
    lt => !selectedTabTypes.has(lt.key)
  )

  // Sync when tabs are updated externally (e.g. Formik reinitialize after save).
  // Safe for the "group ON, all terms off" case: toggling ON with an empty stash
  // leaves tabs.length at 0→0, so the effect never fires and groupEnabled stays true.
  useEffect(() => {
    setGroupEnabled(tabs.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length])

  const getTabOption = (loanType: string) =>
    tabs.find(option => option.type === loanType)

  const updateTabs = (nextTabs: RateCardsConfigTabOption[]) => {
    setDetails({ ...details, tabs: { ...details.tabs, [group]: nextTabs } })
  }

  const handleGroupToggle = (value: boolean) => {
    setGroupEnabled(value)
    if (!value) {
      setStashedTabs([...tabs])
      updateTabs([])
      return
    }
    updateTabs(stashedTabs)
    setStashedTabs([])
  }

  const handleLoanToggle = (
    loanType: string,
    defaultLabel: string,
    nextEnabled: boolean
  ) => {
    const filtered = details.tabs[group].filter(o => o.type !== loanType)
    const next = nextEnabled
      ? [...filtered, { type: loanType, label: defaultLabel }]
      : filtered
    updateTabs(next)
  }

  const handleMoveLoan = (loanType: string, direction: -1 | 1) => {
    const next = moveTabOption(selectedTabs, loanType, direction)
    if (next === selectedTabs) return
    updateTabs(next)
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const next = reorderTabOptions(
      selectedTabs,
      result.source.index,
      result.destination.index
    )
    if (next === selectedTabs) return
    updateTabs(next)
  }

  const handleLabelChange = (
    loanType: string,
    defaultLabel: string,
    nextLabel: string
  ) => {
    const next = details.tabs[group].map(o =>
      o.type === loanType
        ? { ...o, label: nextLabel.trim() || defaultLabel }
        : o
    )
    updateTabs(next)
  }

  if (loanTypes.length === 0) {
    return (
      <ConfigAdminPanel>
        <div className="px-4 py-4">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="mt-1 text-sm text-gray-400">
            No {label.toLowerCase()} scenarios configured. Open Settings to add{" "}
            {label.toLowerCase()} scenario preferences.
          </p>
        </div>
      </ConfigAdminPanel>
    )
  }

  return (
    <ConfigAdminPanel>
      <ConfigAdminToggleRow
        label={label}
        description={`Control which ${label.toLowerCase()} loan types appear on the rate cards`}
        enabled={groupEnabled}
        onChange={handleGroupToggle}
        last={false}
      />
      {groupEnabled && (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`rate-card-${group}-terms`}>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {selectedLoanTypes.map((lt, index) => {
                    return (
                      <Draggable
                        key={lt.key}
                        draggableId={`rate-card-${group}-${lt.key}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={
                              snapshot.isDragging
                                ? "rounded-md bg-white shadow-md"
                                : "bg-white"
                            }
                          >
                            <TermLoanRow
                              loanKey={lt.key}
                              defaultLabel={lt.label}
                              labelValue={
                                getTabOption(lt.key)?.label ?? lt.label
                              }
                              enabled
                              canMoveUp={index > 0}
                              canMoveDown={index < selectedTabs.length - 1}
                              dragHandleProps={provided.dragHandleProps}
                              onMoveUp={() => handleMoveLoan(lt.key, -1)}
                              onMoveDown={() => handleMoveLoan(lt.key, 1)}
                              onLabelChange={v =>
                                handleLabelChange(lt.key, lt.label, v)
                              }
                              onEnabledChange={v =>
                                handleLoanToggle(lt.key, lt.label, v)
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {unselectedLoanTypes.map(lt => (
            <TermLoanRow
              key={lt.key}
              loanKey={lt.key}
              defaultLabel={lt.label}
              labelValue={lt.label}
              enabled={false}
              canMoveUp={false}
              canMoveDown={false}
              onMoveUp={() => handleMoveLoan(lt.key, -1)}
              onMoveDown={() => handleMoveLoan(lt.key, 1)}
              onLabelChange={v => handleLabelChange(lt.key, lt.label, v)}
              onEnabledChange={v => handleLoanToggle(lt.key, lt.label, v)}
            />
          ))}
        </div>
      )}
    </ConfigAdminPanel>
  )
}

export default LoanTypePanel
